import { useEffect, useRef } from 'react';

import { sendBrochureBeacon } from '../utils/brochureTracking';

const MIN_DWELL_SECONDS = 2;

// Records how long a brochure subpane stayed open as a first-party dwell beacon
// to our Drupal DB (was a Fathom trackEvent). The dwell is measured from mount
// to teardown — navigating to another subpane unmounts this one, firing the
// beacon. Source-agnostic about the subject:
//   - node detail views pass { nid } → a 'view' beacon carrying contentNid;
//   - the About pane passes { type: 'about', label, target } (bucket title/key)
//     → an 'about' beacon (buckets aren't nodes).
// Tracking is gated on `tracking` (the brochure creds from the response);
// without it (or without a subject) the hook is inert.
const useDetailViewTracking = ({
  nid, tracking, type = 'view', label, target,
}) => {
  const startedAtRef = useRef(null);
  const hasTracking = !!(tracking && tracking.endpoint && tracking.token && tracking.identity);
  const hasSubject = !!(nid || label || target);

  useEffect(() => {
    if (!hasSubject || !hasTracking) return undefined;

    startedAtRef.current = Date.now();

    return () => {
      const startedAt = startedAtRef.current;
      startedAtRef.current = null;
      if (!startedAt) return;

      const elapsedSeconds = Math.round((Date.now() - startedAt) / 1000);
      if (elapsedSeconds < MIN_DWELL_SECONDS) return;

      sendBrochureBeacon(type, {
        tracking,
        contentNid: nid,
        dwellSeconds: elapsedSeconds,
        label,
        target,
      });
    };
  }, [nid, hasTracking, hasSubject, tracking, type, label, target]);
};

export default useDetailViewTracking;
